import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useUsageLimits } from '../../context/UsageLimitsContext';

/**
 * UsageLimitsPanel - Configuration panel for usage limits (SAT-110)
 *
 * Features:
 * - Configure daily/monthly token and cost limits
 * - Toggle soft/hard limits
 * - Set warning thresholds
 * - Show current usage vs limits
 */
function UsageLimitsPanel() {
  const { isDark } = useTheme();
  const {
    limits,
    currentUsage,
    updateLimits,
    getUsagePercentages,
    getResetInfo,
  } = useUsageLimits();

  const percentages = getUsagePercentages();
  const resetInfo = getResetInfo();

  const formatNumber = (num) => num?.toLocaleString() || '0';
  const formatCost = (cost) => `$${(cost || 0).toFixed(2)}`;

  // Progress bar component
  const ProgressBar = ({ percentage, label, current, limit, type = 'tokens' }) => {
    const getColor = (pct) => {
      if (pct >= 100) return 'bg-red-500';
      if (pct >= limits.criticalThreshold) return 'bg-orange-500';
      if (pct >= limits.warningThreshold) return 'bg-yellow-500';
      return 'bg-green-500';
    };

    return (
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{label}</span>
          <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
            {type === 'cost' ? formatCost(current) : formatNumber(current)} / {type === 'cost' ? formatCost(limit) : formatNumber(limit)}
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div
            className={`h-full ${getColor(percentage)} transition-all duration-300`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px]">
          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
            {percentage.toFixed(1)}% used
          </span>
          <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
            {type === 'cost'
              ? formatCost(limit - current)
              : formatNumber(limit - current)} remaining
          </span>
        </div>
      </div>
    );
  };

  // Limit toggle with input
  const LimitConfig = ({ label, enabled, enabledKey, value, valueKey, type = 'number', step, min, max, description }) => (
    <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1">
          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</span>
          {description && (
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
          )}
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => updateLimits({ [enabledKey]: e.target.checked })}
            className="sr-only peer"
          />
          <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:rtl:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:inset-s-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-decisiv-blue"></div>
        </label>
      </div>
      {enabled && (
        <div className="flex items-center gap-2 mt-2">
          {type === 'cost' && <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>$</span>}
          <input
            type="number"
            value={value}
            onChange={(e) => updateLimits({ [valueKey]: parseFloat(e.target.value) || 0 })}
            step={step}
            min={min}
            max={max}
            className={`flex-1 px-3 py-1.5 text-sm rounded-md border focus:outline-hidden focus:ring-1 focus:ring-decisiv-blue ${
              isDark
                ? 'bg-gray-600 text-white border-gray-500'
                : 'bg-white text-gray-900 border-gray-300'
            }`}
          />
          {type !== 'cost' && <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>tokens</span>}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Current Usage Section */}
      <div>
        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Current Usage
        </h3>
        <div className="space-y-4">
          {limits.dailyTokenLimitEnabled && (
            <ProgressBar
              label="Daily Tokens"
              percentage={percentages.dailyTokens}
              current={currentUsage.dailyTokens}
              limit={limits.dailyTokenLimit}
              type="tokens"
            />
          )}
          {limits.dailyCostLimitEnabled && (
            <ProgressBar
              label="Daily Spending"
              percentage={percentages.dailyCost}
              current={currentUsage.dailyCost}
              limit={limits.dailyCostLimit}
              type="cost"
            />
          )}
          {limits.monthlyTokenLimitEnabled && (
            <ProgressBar
              label="Monthly Tokens"
              percentage={percentages.monthlyTokens}
              current={currentUsage.monthlyTokens}
              limit={limits.monthlyTokenLimit}
              type="tokens"
            />
          )}
          {limits.monthlyCostLimitEnabled && (
            <ProgressBar
              label="Monthly Spending"
              percentage={percentages.monthlyCost}
              current={currentUsage.monthlyCost}
              limit={limits.monthlyCostLimit}
              type="cost"
            />
          )}
          {!limits.dailyTokenLimitEnabled && !limits.dailyCostLimitEnabled &&
           !limits.monthlyTokenLimitEnabled && !limits.monthlyCostLimitEnabled && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No limits enabled. Configure limits below to track usage.
            </p>
          )}
        </div>
      </div>

      {/* Reset Timing */}
      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
        <h4 className={`text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Reset Schedule
        </h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Daily resets in: </span>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>{resetInfo.dailyResetIn}</span>
          </div>
          <div>
            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Monthly resets in: </span>
            <span className={isDark ? 'text-white' : 'text-gray-900'}>{resetInfo.monthlyResetIn}</span>
          </div>
        </div>
      </div>

      {/* Daily Limits */}
      <div>
        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Daily Limits
        </h3>
        <div className="space-y-3">
          <LimitConfig
            label="Token Limit"
            description="Maximum tokens per day"
            enabled={limits.dailyTokenLimitEnabled}
            enabledKey="dailyTokenLimitEnabled"
            value={limits.dailyTokenLimit}
            valueKey="dailyTokenLimit"
            step={1000}
            min={0}
          />
          <LimitConfig
            label="Spending Cap"
            description="Maximum daily spending"
            enabled={limits.dailyCostLimitEnabled}
            enabledKey="dailyCostLimitEnabled"
            value={limits.dailyCostLimit}
            valueKey="dailyCostLimit"
            type="cost"
            step={0.5}
            min={0}
          />
        </div>
      </div>

      {/* Monthly Limits */}
      <div>
        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Monthly Limits
        </h3>
        <div className="space-y-3">
          <LimitConfig
            label="Token Limit"
            description="Maximum tokens per month"
            enabled={limits.monthlyTokenLimitEnabled}
            enabledKey="monthlyTokenLimitEnabled"
            value={limits.monthlyTokenLimit}
            valueKey="monthlyTokenLimit"
            step={10000}
            min={0}
          />
          <LimitConfig
            label="Spending Cap"
            description="Maximum monthly spending"
            enabled={limits.monthlyCostLimitEnabled}
            enabledKey="monthlyCostLimitEnabled"
            value={limits.monthlyCostLimit}
            valueKey="monthlyCostLimit"
            type="cost"
            step={5}
            min={0}
          />
        </div>
      </div>

      {/* Warning Settings */}
      <div>
        <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Warning Settings
        </h3>
        <div className="space-y-3">
          <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Hard Limit</span>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Block requests when limit is exceeded
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={limits.hardLimitEnabled}
                  onChange={(e) => updateLimits({ hardLimitEnabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-300 dark:bg-gray-600 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:rtl:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:inset-s-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <label className={`text-xs font-medium block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Warning at (%)
              </label>
              <input
                type="number"
                value={limits.warningThreshold}
                onChange={(e) => updateLimits({ warningThreshold: parseInt(e.target.value) || 80 })}
                min={1}
                max={99}
                className={`w-full px-3 py-1.5 text-sm rounded-md border focus:outline-hidden focus:ring-1 focus:ring-decisiv-blue ${
                  isDark
                    ? 'bg-gray-600 text-white border-gray-500'
                    : 'bg-white text-gray-900 border-gray-300'
                }`}
              />
            </div>
            <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <label className={`text-xs font-medium block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Critical at (%)
              </label>
              <input
                type="number"
                value={limits.criticalThreshold}
                onChange={(e) => updateLimits({ criticalThreshold: parseInt(e.target.value) || 95 })}
                min={1}
                max={100}
                className={`w-full px-3 py-1.5 text-sm rounded-md border focus:outline-hidden focus:ring-1 focus:ring-decisiv-blue ${
                  isDark
                    ? 'bg-gray-600 text-white border-gray-500'
                    : 'bg-white text-gray-900 border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsageLimitsPanel;
